import { useState, useEffect } from 'react';
import { paymentServiceApi } from '@/services/api';

export interface Transaction {
  id: string;
  orderId: string;
  date: string; // Keep as string to support Jalali dates from backend
  amount: number;
  planName: string | null;
  paymentMethod: string;
  status: 'completed' | 'pending' | 'failed' | 'cancelled';
  currency: string;
  description?: string;
}

export interface Plan {
  id: string;
  name: string;
  price: {
    monthly: number;
    yearly: number;
  };
  features: string[];
  popular?: boolean;
  recommended?: boolean;
  description: string;
  maxBooks?: number;
  supportType: string;
}

export interface Subscription {
  planId: string;
  planName: string;
  status: 'active' | 'expired' | 'cancelled';
  startDate: string;
  endDate: string;
  nextBillingDate?: string;
  billingCycle: 'monthly' | 'yearly';
  amount: number;
}

export const usePayments = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sumSuccess, setSumSuccess] = useState<number>(0);
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);

        type PaymentsResponse = {
          count: number;
          sum_success: string; // decimal as string
          items: Array<{
            plan_name: string | null;
            amount: string; // decimal as string
            created_at: string;
            status: string; // completed | failed | pending | cancelled
            order_id: string;
            status_code: string | null;
            payment_date: string; // e.g., Jalali: 1404/06/05
          }>;
        };

        const res = await paymentServiceApi.get<PaymentsResponse>('/payments/me');
        if (!res.success) {
          throw new Error(res.error || 'خطا در دریافت اطلاعات پرداخت');
        }

        const mapped: Transaction[] = res.data.items.map((it) => {
          const statusLower = (it.status || '').toLowerCase();
          let status: Transaction['status'] = 'pending';
          if (statusLower === 'completed') status = 'completed';
          else if (statusLower === 'failed') status = 'failed';
          else if (statusLower === 'cancelled') status = 'cancelled';
          else if (statusLower === 'pending') status = 'pending';

          return {
            id: it.order_id, // Use order_id as main identifier
            orderId: it.order_id,
            date: it.payment_date, // keep as provided (Jalali)
            amount: Number.parseFloat(it.amount),
            planName: it.plan_name,
            paymentMethod: 'درگاه زیبال',
            status,
            currency: 'تومان',
            description: undefined,
          };
        });

        setTransactions(mapped);
        setTotalCount(res.data.count);
        setSumSuccess(Number.parseFloat(res.data.sum_success || '0'));
        // Keep other states untouched to preserve hook API
        setPlans([]);
        setCurrentSubscription(null);
      } catch (err) {
        setError('خطا در بارگذاری اطلاعات');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const purchasePlan = async (planId: string, billingCycle: 'monthly' | 'yearly') => {
    try {
      setLoading(true);
      
      // Simulate API call for purchase
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const plan = plans.find(p => p.id === planId);
      if (!plan) throw new Error('پلن یافت نشد');

      const newTransaction: Transaction = {
        id: Date.now().toString(),
        orderId: `BKA-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        amount: plan.price[billingCycle],
        planName: plan.name,
        paymentMethod: 'کارت بانکی',
        status: 'completed',
        currency: 'تومان',
        description: `خرید ${plan.name} - ${billingCycle === 'monthly' ? 'ماهانه' : 'سالانه'}`
      };

      setTransactions(prev => [newTransaction, ...prev]);
      
      // Update subscription
      const newSubscription: Subscription = {
        planId,
        planName: plan.name,
        status: 'active',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + (billingCycle === 'monthly' ? 30 : 365) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        nextBillingDate: new Date(Date.now() + (billingCycle === 'monthly' ? 30 : 365) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        billingCycle,
        amount: plan.price[billingCycle]
      };

      setCurrentSubscription(newSubscription);
      
      return { success: true, transaction: newTransaction };
    } catch (err) {
      setError('خطا در خرید پلن');
      return { success: false, error: 'خطا در خرید پلن' };
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async () => {
    try {
      setLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (currentSubscription) {
        setCurrentSubscription({
          ...currentSubscription,
          status: 'cancelled'
        });
      }
      
      return { success: true };
    } catch (err) {
      setError('خطا در لغو اشتراک');
      return { success: false, error: 'خطا در لغو اشتراک' };
    } finally {
      setLoading(false);
    }
  };

  const exportTransactions = (format: 'csv' | 'pdf' = 'csv') => {
    try {
      if (format === 'csv') {
        const csvContent = [
          ['تاریخ', 'مبلغ', 'پلن', 'روش پرداخت', 'وضعیت', 'توضیحات'].join(','),
          ...transactions.map(t => [
            new Date(t.date).toLocaleDateString('fa-IR'),
            t.amount.toString(),
            t.planName,
            t.paymentMethod,
            t.status === 'completed' ? 'تکمیل شده' : t.status === 'pending' ? 'در انتظار' : 'ناموفق',
            t.description || ''
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `payment-history-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
      }
      
      return { success: true };
    } catch (err) {
      setError('خطا در دانلود فایل');
      return { success: false, error: 'خطا در دانلود فایل' };
    }
  };

  const getDiscountPercentage = (billingCycle: 'monthly' | 'yearly', plan: Plan) => {
    if (billingCycle === 'yearly') {
      const monthlyTotal = plan.price.monthly * 12;
      const yearlyPrice = plan.price.yearly;
      return Math.round(((monthlyTotal - yearlyPrice) / monthlyTotal) * 100);
    }
    return 0;
  };

  return {
    transactions,
    plans,
    currentSubscription,
    loading,
    error,
    purchasePlan,
    cancelSubscription,
    exportTransactions,
    getDiscountPercentage,
    totalCount,
    sumSuccess
  };
};
